import { push } from "connected-react-router";
import React, { useMemo, useState } from "react";
import { APPLICATION_MODE_NEW, ROUTE_PATH } from "../../../app/constants";
import { useDispatch, useSelector } from "react-redux";
import { CanadaProvinceList } from "../../../app/constants";
import { ERROR_STRING, VALIDATION_ERROR, VALIDATION_TYPE } from "../../../app/errors";
import DateSelectionForm from "../../../components/DateSelectionForm";
import { ErrorMark, ErrorWithString } from "../../../components/RegularComponents";
import TopNavigationBar from "../../TopNavigationBar";
import styles from "../mra.module.scss";
import { saveBusinessLicenceApplicationData } from "../../../redux/slice/businessLicenceApplicationSlice";
import { applicationTypeFromLicenceSubType } from "../../../redux/slice/applicationsUtils";
import { exemptProvinces } from "../HasMortgageBrokerLicenceInProvinces";
import { GetProviceCodeFromName, ValidateString } from "../../../app/utils";
import { usePathApplication } from "../../../redux/hooks/usePathApplication";
import { agencyApplicationSelector } from "../../../redux/selector/agencyApplicationSelector";
import { setMortgageProperty } from "../../../redux/slice/primaryAgencyApplicationSlice";

const MORTGAGE_APPLICATION_PROPERTY = "licenceHistoryData";

export const MainAgentMortgageBrokerInfo = (props) => {
  const { application } = usePathApplication();
  const { agencyApplication } = useSelector(agencyApplicationSelector) || {};
  const licencingSubType = agencyApplication?.licencingSubType;
  const { isModal = false, onEditSave = () => {} } = props;
  const dispatch = useDispatch();

  const applicationMode = APPLICATION_MODE_NEW;
  const [applicationSaveError, setApplicationSaveError] = useState(false);

  const applicationType = applicationTypeFromLicenceSubType(licencingSubType);

  const licenceHistoryData = useMemo(() => {
    return agencyApplication.businessLicenceData?.licenceHistoryData ?? {};
  }, [agencyApplication]);

  //Setup useState for fields
  const [province, setProvince] = useState(licenceHistoryData.province);
  const [licenceNumber, setLicenceNumber] = useState(licenceHistoryData.licenceNumber);
  const [endDate, setEndDate] = useState(licenceHistoryData.endDate);

  const [showValidationError, setShowValidationError] = useState(false);

  const [provinceError, setProvinceError] = useState(VALIDATION_ERROR.NO_ERROR);
  const [licenceNumberError, setLicenceNumberError] = useState(VALIDATION_ERROR.NO_ERROR);
  const [expiryDateError, setExpiryDateError] = useState(VALIDATION_ERROR.NO_ERROR);

  const validateData = () => {
    setProvinceError(VALIDATION_ERROR.NO_ERROR);
    setLicenceNumberError(VALIDATION_ERROR.NO_ERROR);
    setExpiryDateError(VALIDATION_ERROR.NO_ERROR);

    let retVal = true;

    const ret1 = ValidateString(
      GetProviceCodeFromName(province),
      VALIDATION_TYPE.CANADA_PROVINCE_CODE
    );

    if (ret1 !== VALIDATION_ERROR.NO_ERROR) {
      setProvinceError(ret1);
      retVal = false;
    }

    const ret2 = ValidateString(licenceNumber, VALIDATION_TYPE.STRING_NON_EMPTY);
    if (ret2 !== VALIDATION_ERROR.NO_ERROR) {
      setLicenceNumberError(ret2);
      retVal = false;
    }

    const ret3 = ValidateString(endDate, VALIDATION_TYPE.STRING_NON_EMPTY);
    if (ret3 !== VALIDATION_ERROR.NO_ERROR) {
      setExpiryDateError(ret3);
      retVal = false;
    }

    return retVal;
  };

  const handleNext = () => {
    setApplicationSaveError(false);

    if (!validateData()) {
      setShowValidationError(true);
      return;
    }

    const update = {
      key: MORTGAGE_APPLICATION_PROPERTY,
      value: {
        businessLicenceType: null,
        endDate: endDate,
        province: province,
        licenceNumber: licenceNumber,
        startDate: null
      }
    };
    dispatch(setMortgageProperty(update));
    
    dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
      .then((data) => {
        dispatch(
          !isModal
            ? push(
                `/${application}${ROUTE_PATH.MRA_ADD_MAIN_AGENT_CONFIRM_COMPLETION_REGULATORY_INFO}`
              )
            : onEditSave()
        );
      })
      .catch((err) => {
        setApplicationSaveError(true);
      });
  };


  const provinceOptionElements = useMemo(() => {
    const elems = [];

    elems.push(
      <option key={"unknown"} value={"Select unknown"}>
        {"Select province"}
      </option>
    );

    Object.keys(CanadaProvinceList).forEach((key) => {
      const province = CanadaProvinceList[key];
      if (exemptProvinces.find((p) => p === province)) {
        elems.push(
          <option key={key} value={province}>
            {province}
          </option>
        );
      }
    });

    return elems;
  }, []);

  const title = "Provide information about 1 of your current mortgage broker licences";

  return (
    <div className={!isModal ? "container" : ""}>
      {!isModal && (
        <>
          <TopNavigationBar
            {...props}
            applicationType={applicationType}
            applicationMode={applicationMode}
          />
          {/* Title */}
          <div className="row">
            <div className="col-xs-12 col-lg-10">
              <h1>{title}</h1>
            </div>
            <div className="topMarginMedium">
              Nova Scotia requires a licensing history letter from the province you choose.
            </div>
            <div className="bottomMarginMedium">
              Contact the province to request this letter and have them send it to mortgageregulation@novascotia.ca
            </div>
          </div>
        </>
      )}

      {/* Province Select section */}
      <div className="row">
        <div className="col-lg-8">
          <div
            className={
              "ns-fieldset" +
              (showValidationError && provinceError !== VALIDATION_ERROR.NO_ERROR
                ? " has-error ns-fieldset-message-error"
                : "")
            }
          >
            <label
              id={"mra-province-select-label"}
              className="ns-label"
              htmlFor={"mra-province-select-input"}
            >
              Province
            </label>
            {showValidationError && provinceError !== VALIDATION_ERROR.NO_ERROR && (
              <span
                id={"mra-province-select-input-error"}
                className="ns-error-message fieldset-validation-error"
              >
                {ErrorWithString(ERROR_STRING.CONTACT_DETAILS_PROVINCE_EMPTY)}
              </span>
            )}
            <div className="select fw-25 inlineDisplay">
              <select
                className={provinceError !== VALIDATION_ERROR.NO_ERROR ? "select--error" : ""}
                style={{ width: "25em" }}
                id={"mra-province-select-input"}
                name={"mra-province-select-input"}
                value={province ?? ""}
                onChange={(e) => {
                  setProvince(e.target.value);
                  setProvinceError(VALIDATION_ERROR.NO_ERROR);
                }}
              >
                {provinceOptionElements}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Licence Number */}
      <div className="row">
        <div className="col-lg-8">
          <div
            className={
              "ns-fieldset" +
              (showValidationError && licenceNumberError !== VALIDATION_ERROR.NO_ERROR
                ? " has-error ns-fieldset-message-error"
                : "")
            }
          >
            <fieldset className="ns-fieldset">
              <label
                id={"mortgage-broker-licence-number-label"}
                className="ns-label"
                autoComplete="postal-code"
                htmlFor={"mortgage-broker-licence-number-text"}
              >
                Licence number
              </label>
              {showValidationError && licenceNumberError !== VALIDATION_ERROR.NO_ERROR && (
                <span
                  id={"mortgage-broker-licence-number-text-error"}
                  className="ns-error-message field-validation-error"
                >
                  {ErrorWithString(ERROR_STRING.LICENCE_NUMBER_EMPTY)}
                </span>
              )}
              <input
                type="text"
                className={
                  "fw-25" +
                  (showValidationError && licenceNumberError !== VALIDATION_ERROR.NO_ERROR
                    ? " ns-input--error"
                    : " ns-input")
                }
                id={"mortgage-broker-licence-number-text"}
                name={"mortgage-broker-licence-number-text"}
                value={licenceNumber}
                onChange={(e) => {
                  setLicenceNumber(e.target.value);
                  setLicenceNumberError(VALIDATION_ERROR.NO_ERROR);
                }}
              />
            </fieldset>
          </div>
        </div>
      </div>
      {/* Date */}
      <div className="row">
        <div className="col-lg-8">
          <div
            className={
              "ns-fieldset" +
              (showValidationError && expiryDateError !== VALIDATION_ERROR.NO_ERROR
                ? " has-error ns-fieldset-message-error"
                : "")
            }
          >
            <fieldset className="ns-fieldset">
              <label className="ns-label" id={"mortgage-broker-licence-info-expiry-date-label"}>
                Expiry date
              </label>
              <DateSelectionForm
                id={"mortgage-broker-licence-info-expiry-date"}
                date={endDate}
                showValidationError={showValidationError}
                onDateChange={(date) => {
                  setEndDate(date);
                  setExpiryDateError(VALIDATION_ERROR.NO_ERROR);
                }}
              />
            </fieldset>
          </div>
        </div>
      </div>

      {/* Save and continue */}
      <div className="row">
        <div className={"col-lg-2" + (applicationSaveError ? " has-error" : "")}>
          <button
            className={`${styles["question_button"]} btn-lg btn-default`}
            onClick={handleNext}
            name={"Save and next"}
          >
             {isModal ? "Save and close" : "Save and next"}
          </button>
          {applicationSaveError && !isModal && ErrorMark()}
          {applicationSaveError && !isModal && (
            <div className="ns-error-message help-block topMarginNormal">
              <span className="ns-visually-hidden">Error:</span>
              {ERROR_STRING.APPLICATION_SAVE_ERROR}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
